import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { LogOut, Trash2, Search, ChevronLeft, ChevronRight, Users, Calendar, ClipboardList, FileEdit, Ticket, MailQuestion } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_TOKEN_KEY = "hp_admin_token";

function getAdminToken() {
  return localStorage.getItem(ADMIN_TOKEN_KEY);
}

function adminHeaders() {
  return { Authorization: `Bearer ${getAdminToken()}` };
}

function formatDate(isoStr) {
  if (!isoStr) return "-";
  try {
    const d = new Date(isoStr);
    return d.toLocaleDateString("nl-NL", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "-";
  }
}

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [filters, setFilters] = useState({ clubs: [], teams: [] });
  const [clubFilter, setClubFilter] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [loginFilter, setLoginFilter] = useState("");

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: 25 });
      if (search) params.set("search", search);
      if (clubFilter) params.set("club", clubFilter);
      if (teamFilter) params.set("team", teamFilter);
      if (loginFilter) params.set("login_filter", loginFilter);
      const res = await axios.get(`${API}/admin/users?${params}`, { headers: adminHeaders() });
      setUsers(res.data.users);
      setTotalPages(res.data.total_pages);
      setTotal(res.data.total);
      setFilters(res.data.filters || { clubs: [], teams: [] });
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        navigate("/sportly-admin", { replace: true });
        return;
      }
      toast.error("Kon gebruikers niet laden");
    } finally {
      setLoading(false);
    }
  }, [page, search, clubFilter, teamFilter, loginFilter, navigate]);

  useEffect(() => {
    if (!getAdminToken()) {
      navigate("/sportly-admin", { replace: true });
      return;
    }
    fetchUsers();
  }, [fetchUsers, navigate]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/users/${deleteTarget.user_id}`, { headers: adminHeaders() });
      toast.success(`${deleteTarget.name || deleteTarget.email} verwijderd`);
      setDeleteTarget(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verwijderen mislukt");
    } finally {
      setDeleting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    navigate("/sportly-admin", { replace: true });
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="admin-dashboard">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-amber-500 rounded-md flex items-center justify-center">
              <span className="text-slate-950 text-xs font-bold">S</span>
            </div>
            <span className="font-bold text-sm tracking-tight">Sportly Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/sportly-admin/health")}
              className="text-slate-300 hover:text-white hover:bg-slate-800"
              data-testid="admin-health-btn"
            >
              <FileEdit className="w-4 h-4 mr-1.5" />
              Health check
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/sportly-admin/auth-events")}
              className="text-slate-300 hover:text-white hover:bg-slate-800"
              data-testid="admin-auth-events-btn"
            >
              <FileEdit className="w-4 h-4 mr-1.5" />
              Audit log
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/sportly-admin/pending")}
              className="text-slate-300 hover:text-white hover:bg-slate-800"
              data-testid="admin-pending-btn"
            >
              <MailQuestion className="w-4 h-4 mr-1.5" />
              Onbevestigd
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/sportly-admin/discount-codes")}
              className="text-slate-300 hover:text-white hover:bg-slate-800"
              data-testid="admin-discount-codes-btn"
            >
              <Ticket className="w-4 h-4 mr-1.5" />
              Kortingscodes
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/sportly-admin/homepage")}
              className="text-slate-300 hover:text-white hover:bg-slate-800"
              data-testid="admin-homepage-cms-btn"
            >
              <FileEdit className="w-4 h-4 mr-1.5" />
              Homepage CMS
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-slate-400 hover:text-white hover:bg-slate-800"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-1.5" />
              Uitloggen
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        {/* Stats row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <StatCard icon={<Users className="w-4 h-4" />} label="Totaal gebruikers" value={total} />
          <StatCard icon={<Calendar className="w-4 h-4" />} label="Met seizoen" value={users.filter(u => u.club_name).length} />
          <StatCard icon={<ClipboardList className="w-4 h-4" />} label="Met planning" value={users.filter(u => u.planning_count > 0).length} />
        </div>

        {/* Filters bar */}
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <form onSubmit={handleSearchSubmit} className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              placeholder="Zoek op naam, e-mail, club of team..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 h-9"
              data-testid="admin-search-input"
            />
          </form>
          <select
            value={clubFilter}
            onChange={(e) => { setClubFilter(e.target.value); setPage(1); }}
            className="h-9 rounded-md border border-slate-700 bg-slate-900 text-sm text-slate-300 px-3 focus:border-amber-500 outline-none"
            data-testid="admin-club-filter"
          >
            <option value="">Alle clubs</option>
            {filters.clubs.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            value={teamFilter}
            onChange={(e) => { setTeamFilter(e.target.value); setPage(1); }}
            className="h-9 rounded-md border border-slate-700 bg-slate-900 text-sm text-slate-300 px-3 focus:border-amber-500 outline-none"
            data-testid="admin-team-filter"
          >
            <option value="">Alle teams</option>
            {filters.teams.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select
            value={loginFilter}
            onChange={(e) => { setLoginFilter(e.target.value); setPage(1); }}
            className="h-9 rounded-md border border-slate-700 bg-slate-900 text-sm text-slate-300 px-3 focus:border-amber-500 outline-none"
            data-testid="admin-login-filter"
          >
            <option value="">Alle activiteit</option>
            <option value="active">Actief (30d)</option>
            <option value="inactive">Inactief (30d+)</option>
            <option value="never">Nooit ingelogd</option>
          </select>
        </div>

        {/* Table */}
        <div className="rounded-lg border border-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead>
                <tr className="bg-slate-900/60 border-b border-slate-800">
                  <th className="text-left px-3 py-2.5 font-medium text-slate-400 whitespace-nowrap">E-mail</th>
                  <th className="text-left px-3 py-2.5 font-medium text-slate-400 whitespace-nowrap">Naam</th>
                  <th className="text-left px-3 py-2.5 font-medium text-slate-400 whitespace-nowrap">Club</th>
                  <th className="text-left px-3 py-2.5 font-medium text-slate-400 whitespace-nowrap">Team</th>
                  <th className="text-left px-3 py-2.5 font-medium text-slate-400 whitespace-nowrap">Managers</th>
                  <th className="text-center px-3 py-2.5 font-medium text-slate-400 whitespace-nowrap">Spelers</th>
                  <th className="text-center px-3 py-2.5 font-medium text-slate-400 whitespace-nowrap">Wedstrijden</th>
                  <th className="text-center px-3 py-2.5 font-medium text-slate-400 whitespace-nowrap">Planning</th>
                  <th className="text-left px-3 py-2.5 font-medium text-slate-400 whitespace-nowrap">Aangemeld</th>
                  <th className="text-left px-3 py-2.5 font-medium text-slate-400 whitespace-nowrap">Laatste login</th>
                  <th className="text-center px-3 py-2.5 font-medium text-slate-400 whitespace-nowrap">Actie</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={11} className="text-center py-12 text-slate-500">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-slate-600 border-t-amber-500 rounded-full animate-spin" />
                        Laden...
                      </div>
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="text-center py-12 text-slate-500">
                      Geen gebruikers gevonden
                    </td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr
                      key={u.user_id}
                      className="border-b border-slate-800/50 hover:bg-slate-900/40 transition-colors"
                      data-testid={`admin-user-row-${u.user_id}`}
                    >
                      <td className="px-3 py-2.5 text-slate-300 max-w-[200px] truncate">{u.email}</td>
                      <td className="px-3 py-2.5 text-white font-medium whitespace-nowrap">{u.name || "-"}</td>
                      <td className="px-3 py-2.5 text-slate-300 whitespace-nowrap">{u.club_name || "-"}</td>
                      <td className="px-3 py-2.5 text-slate-300 whitespace-nowrap">{u.team_name || "-"}</td>
                      <td className="px-3 py-2.5">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${u.users_status === "Ingevuld" ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-700 text-slate-400"}`}>
                          {u.users_status || "-"}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-center text-slate-300">{u.players_count}</td>
                      <td className="px-3 py-2.5 text-center text-slate-300">{u.matches_count}</td>
                      <td className="px-3 py-2.5 text-center text-slate-300">{u.planning_count}</td>
                      <td className="px-3 py-2.5 text-slate-400 whitespace-nowrap">{formatDate(u.created_at)}</td>
                      <td className="px-3 py-2.5 text-slate-400 whitespace-nowrap">{formatDate(u.last_login_at)}</td>
                      <td className="px-3 py-2.5 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteTarget(u)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 w-7 p-0"
                          data-testid={`admin-delete-btn-${u.user_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4" data-testid="admin-pagination">
            <span className="text-sm text-slate-500">{total} gebruiker{total !== 1 ? "s" : ""}</span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
                className="text-slate-400 hover:text-white hover:bg-slate-800 h-8 w-8 p-0"
                data-testid="admin-prev-page"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-slate-400 px-2">{page} / {totalPages}</span>
              <Button
                variant="ghost"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
                className="text-slate-400 hover:text-white hover:bg-slate-800 h-8 w-8 p-0"
                data-testid="admin-next-page"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Gebruiker verwijderen</DialogTitle>
            <DialogDescription className="text-slate-400">
              Weet je zeker dat je <strong className="text-white">{deleteTarget?.name || deleteTarget?.email}</strong> wilt verwijderen?
              Dit verwijdert het account en alle bijbehorende data.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="ghost"
              onClick={() => setDeleteTarget(null)}
              className="text-slate-400 hover:text-white hover:bg-slate-800"
              data-testid="admin-delete-cancel"
            >
              Annuleren
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-500 text-white"
              data-testid="admin-delete-confirm"
            >
              {deleting ? "Verwijderen..." : "Verwijderen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function StatCard({ icon, label, value }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/50 px-4 py-3 flex items-center gap-3">
      <div className="w-8 h-8 rounded-md bg-amber-500/10 flex items-center justify-center text-amber-500">
        {icon}
      </div>
      <div>
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-lg font-bold text-white leading-tight">{value}</p>
      </div>
    </div>
  );
}
